import { z } from "zod";

// Base validators
const JSONPrimitiveSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);

const timestampFields = {
  createdAt: z.string(),
  updatedAt: z.string(),
};

// Document related schemas
const requiredDocumentSchema = z.object({
  id: z.string(),
  shortName: z.string(),
  description: z.string().nullable(),
  ...timestampFields,
});

const FileType = z.enum(["pdf", "jpeg", "png", "webp", "jpg", "csv", "xlsx", "xls"]);

const sourceDocumentSchema = z.object({
  id: z.string(),
  fileName: z.string(),
  fileType: FileType,
  fileUrl: z.string(),
  fileContentHash: z.string(),
  state: z.string(),
});

// Audit check related schemas
export const auditCheckSchema = z.object({
  id: z.string(),
  name: z.string(),
  agent: z.string(),
  instructions: z.string(),
  tools: z.array(z.string()),
  documents: z.array(requiredDocumentSchema),
  ...timestampFields,
});

// Procedure related schemas
const procedureAttributeSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  description: z.string(),
  matchStrategy: z.string().nullable(),
  weight: z.number().nullable(),
  sourceRowDisplay: z.boolean(),
  ...timestampFields,
});

export const supportColumnSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  dataType: z.string(),
  procedureRequiredDocuments: z.array(requiredDocumentSchema).nullable().default([]),
  ...timestampFields,
});

export const evaluatedColumnSchema = z.object({
  id: z.string(),
  name: z.string(),
  instructions: z.string(),
  dataType: z.string().default("string"),
  dependencies: z.array(supportColumnSchema).default([]),
  ...timestampFields,
});

export const procedureColumnSchema = z.object({
  id: z.string(),
  columnType: z.string(),
  columnOrder: z.number(),
  supportColumn: supportColumnSchema.nullable().optional(),
  evaluatedColumn: evaluatedColumnSchema.nullable().optional(),
  ...timestampFields,
});
export const dryProcedureSchema = z.object({
  id: z.string(),
  name: z.string(),
  ...timestampFields,
});

export const procedureSchema = dryProcedureSchema.extend({
  id: z.string(),
  name: z.string(),
  attributes: z.array(procedureAttributeSchema),
  attributeLookup: z.record(procedureAttributeSchema),
  requiredDocuments: z.array(requiredDocumentSchema),
  checks: z.array(auditCheckSchema),
  columns: z.array(procedureColumnSchema),
  ...timestampFields,
});

export type DryProcedure = z.infer<typeof dryProcedureSchema>;
export type Procedure = z.infer<typeof procedureSchema>;
// Match session related schemas
const documentActionSchema = z.object({
  id: z.string(),
  sourceRowId: z.string(),
  procedureDocumentId: z.string(),
  userId: z.string(),
  action: z.enum(["mark_missing"]).optional(),
  ...timestampFields,
});

const checkResultSchema = z.object({
  id: z.string(),
  checkId: z.string().nullable(),
  evalColumnId: z.string().nullable(),
  reviewerType: z.enum(["human", "system"]),
  userId: z.string().nullable(),
  decision: z.enum(["pass", "fail"]),
  reason: z.string(),
  reviewOrder: z.number(),
  citations: z.array(
    z.object({
      id: z.string(),
      documentId: z.string(),
      documentElement: z.string(),
      attributeIds: z.array(z.string()),
      shortSummary: z.string().nullable(),
      reasonSpan: z.string().nullable(),
      ...timestampFields,
    })
  ),
  ...timestampFields,
});

export const matchItemSchema = z.object({
  id: z.string(),
  extractionId: z.string().nullable(),
  extractionElementId: z.string().nullable(),
  confidenceScore: z.number(),
  assignments: z.array(
    z.object({
      id: z.string(),
      procedureDocumentId: z.string(),
      assignerId: z.string().nullable(),
      ...timestampFields,
    })
  ),
  matchReason: z.record(z.string(), z.number()).optional(),
  sourceDocument: sourceDocumentSchema,
  state: z.enum(["rejected", "approved", "pending"]),
  ...timestampFields,
});

const DocumentState = z.enum(["pending", "processing", "ready"]);

export type UploadedDocument = z.infer<typeof UploadedDocumentSchema>;
export const UploadedDocumentSchema = z.object({
  id: z.string().uuid(),
  fileName: z.string(),
  fileType: FileType,
  fileUrl: z.string(),
  fileContentHash: z.string(),
  extra: z.record(z.unknown()).default({}),
  state: DocumentState,
  ...timestampFields,
});

// Base UploadedDocument schema
export const searchResultSchema = z.object({
  ...UploadedDocumentSchema.shape,
  highlight: z.string(),
});

export const sourceRowSchema = z.object({
  id: z.string(),
  data: z.record(JSONPrimitiveSchema),
  matchItemsState: z.enum(["pending", "processing", "ready"]),
  testsState: z.enum(["pending", "processing", "ready"]),
  requiredDocumentsActions: z.array(documentActionSchema),
  checkResults: z.array(checkResultSchema),
  matchItems: z.array(matchItemSchema),
  ...timestampFields,
});

export const attributeResultSchema = z.object({
  value: z.union([z.string(), z.boolean(), z.number(), z.null()]),
  unit: z.string().nullable(),
  confidence: z.number().nullable(),
  sourceElementId: z.string().nullable(),
});

export const extractionSchema = z.object({
  id: z.string(),
  data: z.record(z.string(), attributeResultSchema),
  ...timestampFields,
});

export const sessionRequiredDocumentTypeSchema = z.object({
  id: z.string(),
  documentId: z.string(),
  name: z.string().nullable(),
  fetchableDocument: UploadedDocumentSchema.nullable(),
  state: z.enum(["approved", "rejected", "pending"]),
  extraction: extractionSchema.nullable(),
  extractions: z.array(extractionSchema),
});

export const drySessionSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  state: z.enum(["pending", "processing", "ready"]),
  documentsState: z.enum(["pending", "processing", "ready"]),
  testsState: z.enum(["pending", "processing", "ready"]),
  ...timestampFields,
});

export const sessionSchema = z.object({
  ...drySessionSchema.shape,
  procedure: procedureSchema,
  documents: z.array(sessionRequiredDocumentTypeSchema),
  sourceRows: z.array(sourceRowSchema),
});

export type AuditSession = z.infer<typeof sessionSchema>;
